import { useEffect, useRef, useState } from "react";
import type { Field } from "../types/recommendation";
/* THROUGH THE CASCADE, NOT FROZEN AT MODULE LOAD (2026-09-01, W3-UIFIX2).
   This file imported the frozen `colors` and `tierStyles`, so every colour in it resolved once to a
   light-mode hex that no media query could reach: on a dark page the drawer opened as a white sheet
   with #1E293B ink and a #16314A "You entered" chip. This is the Show-Your-Work surface — the one
   screen the product's central claim is made on — so it being stuck in the light scheme was the
   most visible remaining instance of the defect W2 swept out of `cma/`, `screens/` and `ui/`.
   `themeVar` and `themeTierStyles` are the same palette and the same five tiers read as `var()`
   with the ratified literal as the fallback, so nothing about the light rendering changes. See
   `design-tokens.ts` for why the tier styles need a second object rather than an edit to the
   first. */
import { themeTierStyles as tierStyles, themeVar as colors } from "../design-tokens";

/**
 * The right-side split drawer — L2 (provenance + how-computed) and L3
 * (underlying rows, subject pinned) of the Show-Your-Work pattern.
 *
 * Fixes a documented legacy gap (remediation backlog UI-06): "the source
 * drawer sets aria-modal but has no focus trap ... nothing confines Tab, so
 * focus walks into the still-tabbable page behind the overlay." This
 * implementation traps Tab/Shift-Tab inside the dialog, closes on Escape,
 * and restores focus to the trigger on close (handled by the caller,
 * ProvenanceChip, via onClose).
 */
export function SourceDrawer({ field, onClose }: { field: Field; onClose: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    const focusables = () =>
      Array.from(
        dialog.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => !el.hasAttribute("disabled"));

    // Move focus into the dialog on open.
    const first = focusables()[0];
    first?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const els = focusables();
      if (els.length === 0) return;
      const firstEl = els[0];
      const lastEl = els[els.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const style = tierStyles[field.tier];
  const isUserEntered = field.tier === "entered";

  return (
    <div
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        /* The scrim stays a dim of the app's own near-black in BOTH schemes, and deliberately:
           it is not a surface colour, it is the amount the page behind is knocked back. Darkening
           it further on a dark page would erase the distinction between the two. */
        background: "rgba(15,23,42,0.35)",
        zIndex: 50,
        display: "flex",
        justifyContent: "flex-end",
      }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="source-drawer-title"
        style={{
          width: "min(420px, 100%)",
          height: "100%",
          background: colors.bgSurface,
          borderLeft: `1px solid ${colors.border}`,
          padding: 24,
          overflowY: "auto",
          boxShadow: "-4px 0 24px rgba(15,23,42,.12)",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <h2 id="source-drawer-title" style={{ fontSize: 18, margin: 0, color: colors.textPrimary }}>
            {field.label}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            // UI-130: measured 22.5x20 on the live build. It is the only pointer route out of the
            // drawer, and on a 390px phone it sits in the corner where a thumb is least accurate.
            //
            // The target is grown by `data-vb-hit`, NOT by sizing this box. Sizing it was tried
            // first — `minWidth/minHeight: 44` with a -12px margin — and it pushed the drawer's
            // whole header 22px taller, because this button and the <h2> are the two children of a
            // flex row and the button became the tall one. Rendered before/after and caught it.
            // The attribute leaves the layout box at 22.5x20 and grows only what a pointer hits.
            data-vb-hit="44"
            style={{ border: "none", background: "none", cursor: "pointer", fontSize: 18, lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            marginTop: 8,
            padding: "3px 8px",
            borderRadius: 4,
            fontSize: 12,
            color: style.fg,
            background: style.bg,
          }}
        >
          {style.label}
        </div>

        {/* L1 recap — source, always present even for a gap. */}
        <Section title="Provenance">
          <Row label="Source" value={field.source} />
          {field.recordId && <Row label="Record ID" value={field.recordId} />}
          {field.accessDate && <Row label="Accessed" value={field.accessDate} />}
          {field.tier === "gap" && field.reason && (
            <p style={{ color: colors.textSecondary, fontSize: 14, marginTop: 8 }}>
              Not found — {field.reason}. Unknown is never shown as zero.
            </p>
          )}
          {isUserEntered && (
            <p style={{ color: colors.textSecondary, fontSize: 13, marginTop: 8 }}>
              You entered this value directly — it did not come from a connected source.
            </p>
          )}
        </Section>

        {/* L2 — how it was computed. Present ONLY when the field carries a state tag. */}
        {field.state && (
          <Section title="How it was computed">
            <Row label="Transformation" value={field.state} />
            {field.how && <p style={{ fontSize: 14, color: colors.textPrimary, marginTop: 6 }}>{field.how}</p>}
            {field.formula && (
              <pre
                style={{
                  fontFamily: "'JetBrains Mono', ui-monospace, monospace",
                  fontSize: 13,
                  background: colors.bgApp,
                  padding: 10,
                  borderRadius: 6,
                  marginTop: 8,
                  overflowX: "auto",
                }}
              >
                {field.formula}
              </pre>
            )}
            {field.inputs && field.inputs.length > 0 && (
              <ul style={{ marginTop: 8, paddingLeft: 18, fontSize: 13 }}>
                {field.inputs.map((inp) => (
                  <li key={inp.ref}>{inp.label}</li>
                ))}
              </ul>
            )}
          </Section>
        )}

        {/* L3 — underlying records, subject pinned. */}
        {field.rows && field.rows.length > 0 && (
          <Section title="Underlying rows">
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <tbody>
                {field.rows.map((row, i) => (
                  <tr
                    key={i}
                    style={{
                      borderTop: `1px solid ${colors.border}`,
                      fontWeight: row.subject ? 600 : 400,
                      background: row.subject ? colors.accentSoft : "transparent",
                    }}
                  >
                    <td style={{ padding: "6px 4px" }}>
                      {row.subject && <span style={{ color: colors.accent }}>Subject · </span>}
                      {row.label}
                    </td>
                    <td style={{ padding: "6px 4px", textAlign: "right", fontFamily: "monospace" }}>
                      {row.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}

        {/* L3 (continued) — "view raw record": the full claim record, not just
            the rendered summary. This IS real (the raw Field data already
            passed to this component, shown as-is) — not a stub pointed at a
            not-yet-built endpoint. A future `/next` wiring pass can replace
            this with a live `record.trace()` fetch without changing the
            affordance's shape. */}
        <Section title="Full claim record">
          <RawRecordToggle field={field} />
        </Section>
      </div>
    </div>
  );
}

function RawRecordToggle({ field }: { field: Field }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        style={{
          border: `1px solid ${colors.border}`,
          background: colors.bgApp,
          borderRadius: 6,
          padding: "6px 10px",
          fontSize: 13,
          cursor: "pointer",
          color: colors.accent,
        }}
      >
        {expanded ? "Hide raw record" : "View raw record"}
      </button>
      {expanded && (
        <pre
          style={{
            marginTop: 8,
            fontFamily: "'JetBrains Mono', ui-monospace, monospace",
            fontSize: 12,
            background: colors.bgApp,
            padding: 10,
            borderRadius: 6,
            overflowX: "auto",
          }}
        >
          {JSON.stringify(field, null, 2)}
        </pre>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 20 }}>
      <h3
        style={{
          fontSize: 12,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: colors.textSecondary,
          marginBottom: 6,
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "3px 0" }}>
      <span style={{ color: colors.textSecondary }}>{label}</span>
      <span style={{ color: colors.textPrimary, textAlign: "right" }}>{value}</span>
    </div>
  );
}
